import { ref, watch, reactive, computed } from 'vue';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useMainStore } from '@/stores/mainStore';
import { i18n } from '@/plugins/i18n';
import { addToast } from '@teamworksdev/vuetify';
import type { ApiStoreTeamBudgetGroupAthleteRequest } from '@/types/v2/athletes';
import { createAthlete } from '@/api/v2/athletes';
import { getTeamBudgetQueryKey, getBudgetGroupQueryKey } from '@/utils/queries/budgets';
import { getEligibleAthletesQueryKey } from '@/utils/queries/athletes';
import { useBudgetGroupStore } from '@/stores/budgetGroupStore';
import { useGmApi } from '@/composables/useGmApi';

const t = i18n.global.t;

export interface CreateAthleteFormFields {
  firstName: string;
  lastName: string;
  position: string;
  positionUuid?: string | null;
  rosterSpot: boolean;
  budgetTwrns: string[];
}

interface TabState {
  isValid: boolean;
  formFields?: CreateAthleteFormFields;
  selectedAthletes?: string[];
  budgetTwrns?: string[];
}

interface useAddAthletePanelState {
  budgetGroupTwrn: string;
  isOpen: boolean;
  activeTab: number | null;
  tabStates: Record<number, TabState>;
}

const DEFAULT_CREATE_ATHLETE_FORM: CreateAthleteFormFields = {
  firstName: '',
  lastName: '',
  position: '',
  positionUuid: null,
  rosterSpot: true,
  budgetTwrns: [],
};

export const TAB_EXISTING = 0 as const;
export const TAB_CREATE = 1 as const;


const getDefaultState = (): useAddAthletePanelState => ({
  budgetGroupTwrn: '',
  isOpen: false,
  activeTab: null,
  tabStates: {
    [TAB_EXISTING]: {
      isValid: false,
      selectedAthletes: [],
      budgetTwrns: [],
    },
    [TAB_CREATE]: {
      isValid: false,
      formFields: { ...DEFAULT_CREATE_ATHLETE_FORM },
    },
  },
});

const state = reactive<useAddAthletePanelState>(getDefaultState());

function resetState() {
  Object.assign(state, getDefaultState());
}

const isRendered = ref<boolean>(false);

watch(() => state.isOpen, (isOpen) => {
  if (isOpen && !isRendered.value) isRendered.value = true;
  if (!isOpen) resetState();
});

const isFormValid = computed(() => {
  if (state.activeTab === null) return false;
  return state.tabStates[state.activeTab]?.isValid || false;
});


export function useAddAthletePanel() {
  function open(budgetGroupTwrn: string, defaultBudgetTwrn?: string | null) {
    state.isOpen = true;
    state.budgetGroupTwrn = budgetGroupTwrn;
    state.activeTab = TAB_EXISTING; // Default to first tab
    if (defaultBudgetTwrn) {
      if (state.tabStates[TAB_EXISTING]) {
        state.tabStates[TAB_EXISTING].budgetTwrns = [defaultBudgetTwrn];
      }
      if (state.tabStates[TAB_CREATE]?.formFields) {
        state.tabStates[TAB_CREATE].formFields!.budgetTwrns = [defaultBudgetTwrn];
      }
    }
  }

  function updateTabFormState(tabIndex: number, isValid: boolean, formFields: CreateAthleteFormFields) {
    if (state.tabStates[tabIndex]) {
      state.tabStates[tabIndex].isValid = isValid;
      state.tabStates[tabIndex].formFields = formFields;
    }
  }


  function submitAddExistingAthlete() {
    const athletes = state.tabStates[TAB_EXISTING]?.selectedAthletes;
    if (athletes) {
      mutateAddExistingAthlete(athletes);
      return athletes;
    }
    return null;
  }

  function submitCreateAthlete() {
    const formFields = state.tabStates[TAB_CREATE]?.formFields;
    if (formFields) {
      mutateCreateAthlete(formFields);
      return formFields;
    }
    return null;
  }

  function submit() {
    if (!isFormValid.value || state.activeTab === null) {
      return null;
    }

    if (state.activeTab === TAB_EXISTING) {
      return submitAddExistingAthlete();
    } else if (state.activeTab === TAB_CREATE) {
      return submitCreateAthlete();
    }

    return null;
  }

  const mainStore = useMainStore();
  const teamTwrn = computed(() => mainStore.teamTwrn);
  const queryClient = useQueryClient();
  const budgetGroupStore = useBudgetGroupStore();

  watch(() => budgetGroupStore.budgetGroup?.budgets, (budgets) => {
    const createFormFields = state.tabStates[TAB_CREATE]?.formFields;
    const singleBudget = budgets?.length === 1 ? budgets[0] : undefined;
    if (singleBudget && createFormFields && createFormFields.budgetTwrns.length === 0) {
      createFormFields.budgetTwrns = [singleBudget.twrn];
    }
  });

  // EXISTING ATHLETE MUTATION
  const { restoreDeletedAthlete } = useGmApi();

  const { mutate: mutateAddExistingAthlete, isPending: isAddingExistingAthlete } = useMutation({
    mutationFn: (athletes: string[]) => {
      if (!teamTwrn.value) {
        throw new Error('Team TWRN is required');
      }
      const budgetTwrn = state.tabStates[TAB_EXISTING]?.budgetTwrns?.[0];
      if (!budgetTwrn) {
        throw new Error('Budget TWRN is required');
      }
      return restoreDeletedAthlete(teamTwrn.value, budgetTwrn, { athletes });
    },
    onSuccess: (_result, athletes) => {
      const budgetTwrn = state.tabStates[TAB_EXISTING]?.budgetTwrns?.[0];
      const budgetGroupTwrn = state.budgetGroupTwrn;

      state.isOpen = false;

      addToast({
        title: t('success'),
        description: athletes.length === 1 ? t('athleteAddedSuccessfully') : t('athletesAddedSuccessfully', { count: athletes.length }),
        type: 'success',
      });

      queryClient.invalidateQueries({ queryKey: getTeamBudgetQueryKey(teamTwrn.value, budgetTwrn) });
      queryClient.invalidateQueries({ queryKey: getEligibleAthletesQueryKey(teamTwrn.value, budgetTwrn) });
      queryClient.invalidateQueries({ queryKey: getBudgetGroupQueryKey(teamTwrn.value, budgetGroupTwrn) });
    },
    onError: (error) => {
      console.error('[useAddBudgetGroupAthletePanel] Error restoring athletes:', error);
      addToast({
        title: t('toast.genericError.title'),
        description: t('toast.genericError.description'),
        type: 'error',
      });
    },
  });

  // CREATE ATHLETE MUTATION
  const { mutate: mutateCreateAthlete, isPending: isCreatingAthlete } = useMutation({
    mutationFn: (formFields: CreateAthleteFormFields) => {
      if (!teamTwrn.value) {
        throw new Error('Team TWRN is required');
      }
      if (!state.budgetGroupTwrn) {
        throw new Error('Budget Group TWRN is required');
      }

      if (!formFields.positionUuid) {
        throw new Error('Position is required');
      }

      const formFieldsRequest: ApiStoreTeamBudgetGroupAthleteRequest = {
        first_name: formFields.firstName,
        last_name: formFields.lastName,
        has_roster_spot: formFields.rosterSpot,
        position_id: formFields.positionUuid,
        budget_twrns: formFields.budgetTwrns,
      };

      return createAthlete(teamTwrn.value, state.budgetGroupTwrn, formFieldsRequest);
    },
    onSuccess: (_result, formFields) => {
      if (formFields.budgetTwrns.length === 0) {
        throw new Error('Budget TWRN is required');
      }
      for (const budgetTwrn of formFields.budgetTwrns) {
        queryClient.invalidateQueries({ queryKey: getTeamBudgetQueryKey(teamTwrn.value, budgetTwrn) });
        queryClient.invalidateQueries({ queryKey: getEligibleAthletesQueryKey(teamTwrn.value, budgetTwrn) });
      }
      queryClient.invalidateQueries({ queryKey: getBudgetGroupQueryKey(teamTwrn.value, state.budgetGroupTwrn) });
      addToast({
        description: t('athleteAddedSuccessfully'),
        title: t('success'),
        type: 'success',
      });
      state.isOpen = false;
    },
    onError: (error) => {
      console.error('[useAddAthletePanel] Error creating athlete:', error);
      addToast({
        description: t('toast.athleteCreateError.description'),
        title: t('toast.athleteCreateError.title'),
        type: 'error',
      });
    },
  });

  const isPending = computed(() => isCreatingAthlete.value || isAddingExistingAthlete.value);

  return {
    state,
    isRendered,
    isFormValid,
    resetState,
    open,
    updateTabFormState,
    submit,
    submitCreateAthlete,
    TAB_EXISTING,
    TAB_CREATE,
    isPending,
  };
}
